<template>
  <div class="row mx-0">
    <div class="col-1"></div>
    <div class="col-10">
      <div class="p-0 m-0 mt-4 main-title">警報履歷</div>
      <div style="margin-top: 2rem">
        <commonQueryInput :inputItems="multiInputItems" :immediateInputValues="true" v-model:getValue="multiInputVal"
          v-model:getImmediateValue="immediateMultiInputVal"></commonQueryInput>
      </div>
      <div style="margin-top: 2rem">
        <queryResultTable :queryResultTableItems="queryResultTableItems" />
      </div>
      <div style="margin-top: 2rem">
        <chart chartId="alarmHistory-chart" :styleGroup="{ width: '100%', height: '250px' }" :chartData="chartData"
          :chartType="'customize'" :reDraw="true" />
      </div>
      <div class="text-end" style="margin-bottom: 0.6rem">
        <exportCsv :exportData="alarmHistoryItems" :exportDataTitle="alarmHistoryFields.map((item) => item.label)"
          :exportFileName="exportFileName()" />
      </div>
      <commonTable :fields="alarmHistoryFields" :items="alarmHistoryItems" :theme="'green'" stickyHeader="230px">
        <template #cell(alarmVideo)="data">
          <template v-if="data.item.alarmVideo == ''">
            無
          </template>
          <template v-else>
            {{ data.value }}
          </template>
        </template>
      </commonTable>
    </div>
    <div class="col-1"></div>
  </div>
</template>

<script>
import commonQueryInput from "@/components/commonQueryInput.vue";
import queryResultTable from "@/components/queryResultTable.vue";
import chart from "@/components/chart.vue";
import exportCsv from "@/components/exportCsv.vue";
import commonTable from "@/components/commonTable.vue";
import { computed, inject, ref, watch } from "vue";
import { useStore } from "vuex";
export default {
  components: {
    commonQueryInput,
    queryResultTable,
    chart,
    exportCsv,
    commonTable,
  },
  setup() {
    const store = useStore();

    //getters
    const loginData = computed(() => store.getters["getLogin/get_loginData"]);
    const sideNavPath = computed(
      () => store.getters["sideBarTree/get_sideNavPath"]
    );
    const axiosResult = computed(
      () => store.getters["commonAxios/get_axiosResult"]
    );
    const oper = computed(() => store.getters["selectOptions/get_oper"]);
    const device = computed(() => store.getters["selectOptions/get_device"]);

    //dispatch
    const methods = {
      axiosAction: async (payload) => {
        await store.dispatch("commonAxios/axiosAction", payload);
      },
      triggerLoadingBar: () => {
        store.dispatch("commonLoadingBar/triggerLoadingBar");
      },
      setTimeoutAlert: (alertText) => {
        store.dispatch("commonAlert/setTimeoutAlert", [alertText]);
      },
      set_device: (payload) => {
        store.dispatch("selectOptions/set_device", payload);
      },
    };

    const path = inject("path");
    const multiInputVal = ref();
    const multiInputItems = ref([
      {
        key: "date",
        label: "日期",
        type: "dateBetween", // date/dateBetween/text/select/
      },
      {
        key: "oper",
        label: "製程",
        type: "select",
        checkEmpty: true,
        disableText: "-- 選擇製程 --",
        options: oper,
      },
      {
        key: "deviceID",
        label: "機台",
        type: "select",
        bindKey: "oper",
        checkEmpty: true,
        disableText: "-- 選擇機台 --",
        bindOptions: device,
      },
    ]);
    const immediateMultiInputVal = ref();

    //查詢回來的機台名稱
    const deviceName = ref("");

    //查詢結果表格資料
    const queryResultTableItems = ref([]);

    //圖表X軸資料
    const chartXaxisData = ref([]);
    //圖表Y軸平均警報次數
    const chartYaxisAverageData = ref({
      data: [],
      name: "平均警報次數",
      type: "line",
      smooth: true,
      itemStyle: {
        color: "#FF3300",
      },
    });
    //圖表Y軸資料
    const chartYaxisData = ref([
      //先把異常存進來再存入平均警報次數
      chartYaxisAverageData.value,
    ]);

    //圖表資料
    const chartData = ref({
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
          label: {
            backgroundColor: "#6a7985",
          },
        },
      },
      legend: {
        data: ["平均警報次數"],
        bottom: 0,
      },
      grid: {
        top: "20%",
        left: "3%",
        right: "4%",
        bottom: "12%",
        containLabel: true,
      },
      xAxis: [
        {
          type: "category",
          data: chartXaxisData,
          splitLine: { show: false },
          axisPointer: {
            type: "shadow",
          },
        },
      ],
      yAxis: {
        type: "value",
        name: "單位 次數",
        minInterval: 1,
      },
      series: chartYaxisData,
    });

    //警報履歷資訊
    const alarmHistoryFields = [
      {
        key: "alarmCode",
        label: "警報代碼",
        sortable: true,
        thStyle: {
          "min-width": "125px",
        },
        tdStyle: {
          "font-size": "16px",
          "white-space": "pre-wrap",
        },
      },
      {
        key: "alarmDetail",
        label: "警報代碼說明",
        sortable: true,
        thStyle: {
          "min-width": "200px",
        },
        tdStyle: {
          "font-size": "16px",
          "white-space": "pre-wrap",
        },
      },
      {
        key: "alarmVideo",
        label: "機台影像",
        sortable: true,
        thStyle: {
          "min-width": "110px",
        },
        tdStyle: {
          "font-size": "16px",
        },
      },
      {
        key: "startTime",
        label: "開始時間",
        sortable: true,
        thStyle: {
          "min-width": "200px",
        },
        tdStyle: {
          "font-size": "16px",
        },
      },
      {
        key: "endTime",
        label: "結束時間",
        sortable: true,
        thStyle: {
          "min-width": "200px",
        },
        tdStyle: {
          "font-size": "16px",
        },
      },
      {
        key: "durationTime",
        label: "持續時間",
        sortable: true,
        thStyle: {
          "min-width": "115px",
        },
        tdStyle: {
          "font-size": "16px",
        },
      },
    ];
    const alarmHistoryItems = ref([]);

    //匯出資料的檔案名稱
    const exportFileName = () => {
      let exportFileName = "";
      if (deviceName.value != "") {
        exportFileName =
          "警報履歷_" +
          deviceName.value +
          "_" +
          new Date().getFullYear() +
          ("0" + (new Date().getMonth() + 1)).slice(-2) +
          ("0" + new Date().getDate()).slice(-2);
      }
      return exportFileName;
    };

    //watch 按查詢鍵回傳所有輸入框的值
    watch(
      multiInputVal,
      (newValue) => {
        methods.triggerLoadingBar();
        let params = {};
        params["webPage"] =
          sideNavPath.value.length != 0
            ? sideNavPath.value[sideNavPath.value.length - 1]
            : null;
        params["user"] = loginData.value.account;
        params["vuePage"] = path.value;
        console.log(path.value);
        params["methods"] = "POST";
        params["whichFunction"] = "Query_Alarm_Log";
        params["startTime"] = newValue["date"]["startDate"] + " 00:00:00";
        params["endTime"] = newValue["date"]["endDate"] + " 23:59:59";
        params["oper"] = newValue["oper"];
        params["deviceID"] = newValue["deviceID"];

        methods.axiosAction(params).then(() => {
          console.log(axiosResult.value);
          let result = axiosResult.value;
          let response = result.Response;
          if (response == "ok") {
            let queryTableData = result.QueryTableData;
            deviceName.value = queryTableData[0].query_date[5];
            queryResultTableItems.value = [
              {
                date:
                  queryTableData[0].query_date[0].split(" ")[0] +
                  "~" +
                  queryTableData[0].query_date[1].split(" ")[0],
                site: queryTableData[0].query_date[2],
                oper: queryTableData[0].query_date[3],
                group: queryTableData[0].query_date[4],
                device: deviceName.value,
              },
            ];
            chartXaxisData.value = queryTableData[0].abn_species.data_time;
            chartYaxisAverageData.value.data =
              queryTableData[0].abn_species.line_data;
            let yaxisData = [];
            let abnSpeciesArray = Object.keys(
              queryTableData[0].abn_species.data
            );
            let legendData = ['平均警報次數'];
            abnSpeciesArray.forEach((data) => {
              yaxisData.push({
                data: queryTableData[0].abn_species.data[data].data,
                name: data,
                type: "bar",
              });
              legendData.push(data);
            });
            yaxisData.push(chartYaxisAverageData.value);
            chartYaxisData.value = yaxisData;

            chartData.value.legend.data = legendData;
            alarmHistoryItems.value = queryTableData[0].device_detail_data;
          } else {
            deviceName.value = "";
            chartData.value.legend.data = ['平均警報次數'];
            chartXaxisData.value = { day: [], week: [], month: [] };
            chartYaxisData.value = { day: [], week: [], month: [] };
            alarmHistoryItems.value = [];
            methods.setTimeoutAlert(response);
          }
          methods.triggerLoadingBar();
        });
      },
      { deep: true }
    );

    const startTime = ref("");
    const process = ref("");
    // 查詢機台
    watch(
      immediateMultiInputVal,
      (newValue) => {
        if (newValue.date == "" || newValue.date == null) {
          if (
            newValue.date.startDate == "" ||
            newValue.date.startDate == null
          ) {
            return;
          }
        }
        if (newValue.oper == "" || newValue.oper == null) {
          return;
        }
        if (
          startTime.value == newValue.date.startDate &&
          process.value == newValue.oper
        ) {
          return;
        }
        let params = {};
        params["user"] = loginData.value.account;
        params["vuePage"] = "selectoption";
        params["methods"] = "GET";
        params["whichFunction"] = "GetDeviceOption";
        if (newValue.date != "" || newValue.date != null) {
          if (
            newValue.date.startDate != "" ||
            newValue.date.startDate != null
          ) {
            params["startTime"] = newValue.date.startDate;
          }
        }
        if (newValue.oper != "" || newValue.oper != null) {
          params["process"] = newValue.oper;
        } else {
          params["process"] = "-";
        }
        methods.axiosAction(params).then(() => {
          let result = axiosResult.value;
          if (result["Response"] == "ok") {
            methods.set_device(result.QueryTableData.device);
            startTime.value = newValue.date.startDate;
            process.value = newValue.oper;
          } else {
            let returnData = {};
            returnData[params.process] = [];
            methods.set_device(returnData);
            startTime.value = "";
            process.value = "";
          }
        });
      },
      { deep: true }
    );

    //更新X軸資料
    watch(
      chartXaxisData,
      (newValue) => {
        chartData.value.xAxis[0].data = newValue;
      },
      { deep: true }
    );
    //更新Y軸資料
    watch(
      chartYaxisData,
      (newValue) => {
        chartData.value.series = newValue;
      },
      { deep: true }
    );

    return {
      multiInputVal,
      multiInputItems,
      immediateMultiInputVal,
      queryResultTableItems,
      chartData,
      alarmHistoryFields,
      alarmHistoryItems,
      exportFileName,
    };
  },
};
</script>

<style></style>
